import type {
  IntegrationAccessDraft,
  IntegrationAccessResult,
  IssuedApiKey,
  ProcessingInstructionView,
  PublicApiScope,
} from "@planerius/contracts/public-api"
import type { ReactNode } from "react"
import { useState } from "react"

import { useCabinetAccess } from "../../access/use-access"
import { fetchIntegrations, fetchInstruction, grantAccess, issueKey, revokeKey } from "../../api/integrations.api"
import { reconfirmTwoFactor } from "../../api/mfa.api"
import { isApiProblem } from "../../api/problem"
import { cx } from "../../lib/cx"
import { formatDayMonth, formatTimeOfDay } from "../../lib/format"
import { resolveBranch } from "../../shell/use-branch"
import { Button, Checkbox, Chip, Chips, Skeleton, TextField } from "../../ui"
import { useResource } from "../directories/use-resource"
import {
  type AccessDraftForm,
  SCOPE_CHOICES,
  TERM_CHOICES,
  draftProblem,
  needsInstruction,
  parseAddresses,
  scopesToggled,
} from "./developers-model"
import styles from "./developers.module.css"

type Step = "rights" | "doc" | "key" | "lost"

/**
 * 4-В7 «Дать доступ к данным» — /developers/$id/access (макет «Доступы, вебхуки, печать - П2»,
 * карточка 640): «Права» → «Документ и 2FA» → «Ключ».
 *
 * Документ составляет сервер из выбранных прав, филиалов, срока и цели — салон не пишет
 * юридический текст сам, но видит ровно тот, который принимает: при нажатии сервер собирает
 * его заново и сверяет хеш.
 *
 * Второй фактор — в самом шаге документа, как в макете: «Подтвердите, что это вы: вы
 * открываете доступ к телефонам клиентов». Код проверяется прямо перед выдачей.
 *
 * Ключ показывается один раз. Ушёл, не скопировав, — отдельное состояние: выпустить новый
 * и отозвать этот, потому что он уже действует и без вас.
 */
export function AccessWizardScreen(props: {
  readonly integrationId: string
  readonly onBack: () => void
  readonly onDone: () => void
}): ReactNode {
  const { context } = useCabinetAccess()
  const timeZone = resolveBranch(context, null)?.timezone ?? context.tenant.defaultTimezone
  const isOwner = context.role.code === "owner"
  const list = useResource((signal) => fetchIntegrations(signal), "integrations:wizard")
  const integration = list.data?.items.find((item) => item.id === props.integrationId) ?? null

  const [step, setStep] = useState<Step>("rights")
  const [form, setForm] = useState<AccessDraftForm>({
    scopes: [],
    branchIds: context.branches.length === 1 ? null : [],
    days: 90,
    purpose: "",
    addresses: "",
    dynamicIpRisk: false,
  })
  const [document, setDocument] = useState<ProcessingInstructionView | null>(null)
  const [code, setCode] = useState("")
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [result, setResult] = useState<IntegrationAccessResult | null>(null)
  const [issued, setIssued] = useState<IssuedApiKey | null>(null)
  const [copied, setCopied] = useState(false)
  const [revokedLost, setRevokedLost] = useState(false)

  const update = (patch: Partial<AccessDraftForm>): void => {
    setForm((value) => ({ ...value, ...patch }))
    setProblem(null)
  }
  const pii = needsInstruction(form.scopes)
  const unmasks = form.scopes.includes("clients.pii")
  const denied = pii && !isOwner
  const draft: IntegrationAccessDraft = {
    scopes: [...form.scopes],
    branchIds: form.branchIds === null ? [] : [...form.branchIds],
    expiresInDays: form.days,
    purpose: form.purpose.trim(),
  }

  const toDocument = async (): Promise<void> => {
    const local = draftProblem(form)
    if (local !== null) {
      setProblem(local)
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      setDocument(await fetchInstruction(props.integrationId, draft))
      setStep("doc")
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  const issue = async (): Promise<void> => {
    if (document === null) return
    if (pii && code.trim().length < 6) {
      setProblem("Введите шесть цифр из приложения-аутентификатора.")
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      if (pii) await reconfirmTwoFactor(code.trim(), "totp")
      const addresses = parseAddresses(form.addresses).cidrs
      const done = await grantAccess(props.integrationId, {
        ...draft,
        keyName: integration?.name ?? "ключ интеграции",
        environment: "live",
        allowedCidrs: [...addresses],
        acceptDynamicIpRisk: unmasks && addresses.length === 0 && form.dynamicIpRisk,
        signatureMode: "none",
        instruction: document.required ? { version: document.version, textHash: document.textHash, accept: true } : null,
      })
      setResult(done)
      setIssued(done.issued)
      setStep("key")
    } catch (error) {
      if (!isApiProblem(error)) throw error
      if (error.code === "processing_instruction_changed") {
        // Сменились сутки или реквизиты: показать новый текст и попросить принять его.
        setDocument(await fetchInstruction(props.integrationId, draft))
        setProblem("Текст поручения изменился — прочитайте его ещё раз и примите.")
      } else if (error.code === "mfa_invalid") {
        setProblem("Код не подошёл. Введите новый код из приложения.")
        setCode("")
      } else if (error.code === "offline" || error.code === "timeout") {
        setProblem("Ключ не выпущен. Ничего не изменилось, повторите при связи.")
      } else {
        setProblem(error.detail ?? error.title)
      }
    } finally {
      setBusy(false)
    }
  }

  const reissue = async (): Promise<void> => {
    if (result === null || issued === null) return
    setBusy(true)
    setProblem(null)
    try {
      const addresses = parseAddresses(form.addresses).cidrs
      const next = await issueKey({
        grantId: result.integration.access?.grantId ?? "",
        name: integration?.name ?? "ключ интеграции",
        environment: "live",
        expiresInDays: form.days,
        scopes: [],
        allowedCidrs: [...addresses],
        signatureMode: "none",
        acceptDynamicIpRisk: unmasks && addresses.length === 0 && form.dynamicIpRisk,
      })
      setIssued(next)
      setCopied(false)
      setStep("key")
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  const revokeLost = async (): Promise<void> => {
    if (issued === null) return
    setBusy(true)
    setProblem(null)
    try {
      await revokeKey(issued.key.kid)
      setRevokedLost(true)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  const stepIndex = { rights: 1, doc: 2, key: 3, lost: 3 }[step]
  const name = integration?.name ?? ""

  return (
    <div className={styles.screen}>
      <div className={styles.crumbs}>
        <button type="button" className={styles.crumbLink} onClick={props.onBack}>
          Разработчикам
        </button>
        <span className={styles.crumbSep}>/</span>
        <span className={styles.crumbTitle}>Дать доступ</span>
      </div>
      <div className={styles.page}>
        <section className={cx(styles.card, styles.narrow)} aria-labelledby="access-title">
          <div>
            <h1 id="access-title" className={styles.title}>
              Дать доступ к данным{name.length > 0 ? ` · ${name}` : ""}
            </h1>
            <div className={styles.sub}>
              Данные клиентов открывает только владелец салона — требование правового контура, не интерфейса
            </div>
          </div>
          <ol className={styles.steps} aria-label="Шаги">
            {["1 · Права", "2 · Документ и 2FA", "3 · Ключ"].map((label, index) => (
              <li key={label} className={cx(styles.step, index + 1 <= stepIndex && styles.stepDone)}>
                {label}
              </li>
            ))}
          </ol>

          {list.data === null ? <Skeleton width="100%" height={200} /> : null}

          {list.data !== null && step === "rights" ? (
            <>
              <div className={styles.scopeList}>
                {SCOPE_CHOICES.map((choice) => (
                  <Checkbox
                    key={choice.scope}
                    className={cx(styles.scopeRow, choice.pii === true && styles.scopeRowPii)}
                    checked={form.scopes.includes(choice.scope)}
                    onChange={() => update({ scopes: scopesToggled(form.scopes, choice.scope as PublicApiScope) })}
                  >
                    <span className={cx(styles.scopeTitle, choice.pii === true && styles.scopeTitlePii)}>{choice.title}</span>
                    {choice.sub === undefined ? null : <span className={styles.scopeSub}> — {choice.sub}</span>}
                  </Checkbox>
                ))}
              </div>

              <div className={styles.group}>
                <div className={styles.groupLabel}>Филиалы</div>
                <Chips label="Филиалы">
                  {context.branches.map((branch) => (
                    <Chip
                      key={branch.id}
                      active={form.branchIds !== null && form.branchIds.includes(branch.id)}
                      onClick={() => {
                        const current = form.branchIds ?? []
                        update({
                          branchIds: current.includes(branch.id) ? current.filter((id) => id !== branch.id) : [...current, branch.id],
                        })
                      }}
                    >
                      Только {branch.name}
                    </Chip>
                  ))}
                  <Chip active={form.branchIds === null} onClick={() => update({ branchIds: null })}>
                    Все, включая будущие
                  </Chip>
                </Chips>
                <div className={styles.hint}>Можно пустить подрядчика только в одну точку.</div>
              </div>

              <div className={styles.group}>
                <div className={styles.groupLabel}>Срок ключа</div>
                <Chips label="Срок ключа">
                  {TERM_CHOICES.map((term) => (
                    <Chip key={term.days} active={form.days === term.days} onClick={() => update({ days: term.days })}>
                      {term.label}
                    </Chip>
                  ))}
                </Chips>
                <div className={styles.hint}>
                  Бессрочных ключей не бывает: ключ без срока живёт вечно в чужой переписке и в наследстве уволившегося
                  подрядчика. Напомним за две недели.
                </div>
              </div>

              <TextField
                label="Зачем подрядчику доступ"
                hint="Войдёт в поручение строкой «Цель»: «синхронизация записей с кассой»."
                value={form.purpose}
                maxLength={300}
                onChange={(event) => update({ purpose: event.target.value })}
              />

              <TextField
                label="Адреса, с которых ключ работает"
                hint="Единственное, что защищает после утечки ключа. Если адрес плавающий — отметьте, что понимаете риск: это запишется."
                value={form.addresses}
                placeholder="185.12.44.10, 185.12.44.11"
                onChange={(event) => update({ addresses: event.target.value })}
              />
              {unmasks && parseAddresses(form.addresses).cidrs.length === 0 ? (
                <Checkbox checked={form.dynamicIpRisk} onChange={(checked) => update({ dynamicIpRisk: checked })}>
                  Адрес плавающий — понимаю, что после утечки ключ будет работать откуда угодно
                </Checkbox>
              ) : null}

              {denied ? (
                <div className={styles.denied}>
                  <div className={styles.strong}>Выпуск ключа к данным клиентов подписывает только владелец салона</div>
                  <div className={styles.sub}>
                    Ваша роль — {context.role.name.toLowerCase()}. Это не запрет интерфейса, а требование правового
                    контура. Попросите владельца салона — или оставьте права без данных клиентов.
                  </div>
                </div>
              ) : null}

              {problem === null ? null : (
                <div className={styles.problem} role="alert">
                  {problem}
                </div>
              )}
              <div className={styles.actionsEnd}>
                <Button size="sm" variant="primary" loading={busy} deniedReason={denied ? "Только владелец салона" : undefined} onClick={() => void toDocument()}>
                  Дальше — документ
                </Button>
              </div>
            </>
          ) : null}

          {step === "doc" && document !== null ? (
            <>
              <div className={styles.document}>{document.text}</div>
              {pii ? (
                <div className={styles.secondFactor}>
                  <div className={styles.strong}>
                    Подтвердите, что это вы: {unmasks ? "вы открываете доступ к телефонам клиентов" : "вы открываете доступ к данным клиентов"}
                  </div>
                  <div className={styles.codeRow}>
                    <input
                      className={styles.code}
                      aria-label="Код из приложения"
                      placeholder="Код из приложения"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(event) => {
                        setCode(event.target.value.replace(/\D/g, ""))
                        setProblem(null)
                      }}
                    />
                    <span className={styles.hint}>Разовая проверка, что за компьютером вы.</span>
                  </div>
                </div>
              ) : null}
              {problem === null ? null : (
                <div className={styles.problem} role="alert">
                  {problem}
                </div>
              )}
              <div className={styles.actionsEnd}>
                <Button size="sm" variant="secondary" onClick={() => setStep("rights")}>
                  Назад
                </Button>
                <Button size="sm" variant="primary" loading={busy} onClick={() => void issue()}>
                  {document.required ? "Акцептовать и выпустить ключ" : "Выпустить ключ"}
                </Button>
              </div>
            </>
          ) : null}

          {step === "key" && issued !== null ? (
            <>
              <div className={styles.secret}>
                <div className={styles.secretLabel}>Ключ · показывается один раз</div>
                <div className={styles.secretValue}>{issued.secret}</div>
                <div className={styles.codeRow}>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      void navigator.clipboard.writeText(issued.secret).then(() => setCopied(true))
                    }}
                  >
                    {copied ? "Скопировано" : "Скопировать"}
                  </Button>
                  <span>
                    <b className={styles.strong}>Скопируйте сейчас.</b> Показать ещё раз мы не сможем — только выпустить
                    новый.
                  </span>
                </div>
              </div>
              {result === null ? null : (
                <div className={styles.sub}>
                  {acceptanceLine(result, timeZone)} Ключ действует до {formatDayMonth(issued.key.expiresAt, timeZone)}{" "}
                  {issued.key.expiresAt.slice(0, 4)}.
                </div>
              )}
              <div className={styles.actionsEnd}>
                <Button size="sm" variant="secondary" onClick={() => (copied ? props.onDone() : setStep("lost"))}>
                  Готово
                </Button>
              </div>
            </>
          ) : null}

          {step === "lost" && issued !== null ? (
            <div className={styles.lost}>
              <div className={styles.strong}>Ключ показан один раз и больше не покажется</div>
              <div className={styles.confirmText}>
                {revokedLost
                  ? "Ключ отозван. Выпустите новый, когда подрядчик будет готов его записать."
                  : "Если вы его не сохранили — выпустите новый, а старый отзовите: он уже действует и без вас."}
              </div>
              {problem === null ? null : (
                <div className={styles.problem} role="alert">
                  {problem}
                </div>
              )}
              <div className={styles.actions}>
                {revokedLost ? null : (
                  <Button size="sm" variant="danger" loading={busy} onClick={() => void revokeLost()}>
                    Отозвать этот ключ
                  </Button>
                )}
                <Button size="sm" variant="secondary" loading={busy} onClick={() => void reissue()}>
                  Выпустить новый
                </Button>
                <Button size="sm" variant="ghost" onClick={props.onDone}>
                  К списку
                </Button>
              </div>
            </div>
          ) : null}

          <div className={styles.hint}>
            Ключ не выпускается без документа и подтверждения; при обрыве связи ничего не меняется — повторите.
          </div>
        </section>
      </div>
    </div>
  )
}

/** «Акцепт записан: Алексей Громов · 16 сентября, 13:52 · поручение draft-2026-10 · второй фактор подтверждён.» */
function acceptanceLine(result: IntegrationAccessResult, timeZone: string): string {
  const access = result.integration.access
  if (access === null) return ""
  const at = `${formatDayMonth(access.grantedAt, timeZone)}, ${formatTimeOfDay(access.grantedAt, timeZone)}`
  const instruction = access.instruction
  if (instruction === null) return `Доступ выдан: ${access.grantedBy ?? "—"} · ${at}.`
  return `Акцепт записан: ${access.grantedBy ?? "—"} · ${at} · поручение ${instruction.version}${
    instruction.twoFactorConfirmed ? " · второй фактор подтверждён" : ""
  }.`
}
